import { useState, useEffect, useCallback } from 'react'
import { transcriptionService } from '../services/transcriptionService'
import { useAuth } from '../context/AuthContext'

export const useTranscription = (transcriptionId) => {
  const [transcription, setTranscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user } = useAuth()

  // 获取转录详情
  const fetchTranscription = useCallback(async () => {
    if (!transcriptionId || !user) return

    try {
      setLoading(true)
      setError(null)
      const data = await transcriptionService.getTranscriptionDetails(transcriptionId)
      setTranscription(data)
    } catch (err) {
      console.error('获取转录详情失败:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [transcriptionId, user])

  // 删除转录
  const deleteTranscription = useCallback(async () => {
    if (!transcription) return

    try {
      await transcriptionService.deleteTranscription(
        transcription.id, 
        transcription.audio_file_path
      )
      setTranscription(null)
    } catch (err) {
      console.error('删除转录失败:', err)
      throw err
    }
  }, [transcription])

  // 下载转录结果
  const downloadTranscription = useCallback(() => {
    if (!transcription) return
    transcriptionService.downloadTranscription(transcription)
  }, [transcription])

  // 监听转录状态变化
  useEffect(() => {
    if (!transcriptionId) return

    const subscription = transcriptionService.subscribeToTranscriptionUpdates(
      transcriptionId,
      (updatedTranscription) => {
        setTranscription(prev => ({
          ...prev,
          ...updatedTranscription
        }))
      }
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [transcriptionId])

  // 初始加载
  useEffect(() => {
    fetchTranscription()
  }, [fetchTranscription])

  return {
    transcription,
    loading,
    error,
    refetch: fetchTranscription,
    deleteTranscription,
    downloadTranscription,
    isProcessing: transcription?.status === 'processing',
    isCompleted: transcription?.status === 'completed',
    isFailed: transcription?.status === 'failed'
  }
}

// 获取用户转录列表的Hook
export const useUserTranscriptions = () => {
  const [transcriptions, setTranscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user } = useAuth()

  const fetchTranscriptions = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      setError(null)
      const data = await transcriptionService.getUserTranscriptions(user.id)
      setTranscriptions(data)
    } catch (err) {
      console.error('获取转录列表失败:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchTranscriptions()
  }, [fetchTranscriptions])

  return {
    transcriptions,
    loading,
    error,
    refetch: fetchTranscriptions
  }
}